/**
 * THE MAIN CHAT'S CLIENT — the wire shapes, and the retry identity.
 *
 * ONE MODULE FOR ONE CONVERSATION. The product has exactly one, so there is no
 * conversation picker here, no "new chat", and no id the caller may choose: the
 * server resolves which conversation the Main Chat is and this module carries
 * that id on every request. A client that could name a conversation could name
 * a different one, and then two tabs would be two threads.
 *
 * THE RETRY IDENTITY IS MINTED HERE AND KEPT ONLY AS LONG AS IT IS NEEDED. A
 * send gets a `clientRequestId` before it leaves; if the answer arrives the id
 * is discarded, and if the request fails the id is KEPT so that pressing send
 * again asks the server about the FIRST attempt rather than starting a second
 * one. That is what stops a timeout from becoming a second paid model call and
 * a duplicate pair of messages — and it is why the id is not regenerated on
 * retry, which is the one thing a naive implementation gets wrong.
 */

import { readGateway, type GatewayError, type GatewayResult } from './gateway';

/** WHAT A TURN CARRIED, AS METADATA. The content lives in the material store;
 *  this is what a reload needs to show the attachment that was sent. */
export interface MaterialSummary {
	materialId: string;
	title: string;
	kind: string;
	mediaType: string;
	byteSize: number;
	lineCount: number;
	fileName: string | null;
	extractionStatus: string | null;
	extractionNote: string | null;
}

export interface ChatMessage {
	messageId: string;
	conversationId: string;
	sequence: number;
	role: 'OWNER' | 'SCOUT';
	content: string;
	phrasing: string;
	reasoning: string | null;
	completion: string | null;
	/** Present on a turn the Owner invited with "Scout, cerita"; absent on an
	 *  ordinary answer. The row itself is the same durable message. */
	triggerKind?: string | null;
	refs: string[];
	createdAt: string;
	/** WHAT WAS LAST WRITTEN ABOUT THIS TURN'S ATTEMPT. Absent is the honest
	 *  state for everything written before the column existed.
	 *
	 *  READ `answerOutlook` TO DECIDE WHAT TO SHOW. This field is the durable
	 *  record and it can say `PENDING` about a turn whose answer is already on
	 *  record — a process that died between writing the answer and recording
	 *  that it had leaves exactly that row. */
	answerState?: 'PENDING' | 'ANSWERED' | 'FAILED';
	/** WHAT A READER MAY CONCLUDE — the state, the answer row and the clock
	 *  together. Only the server can compute it, because only the server can see
	 *  whether the answer row exists.
	 *
	 *    ANSWERED   the answer is on record; nothing is outstanding
	 *    FAILED     the attempt recorded a failure; retrying is safe
	 *    IN_FLIGHT  an attempt may genuinely still be running — WAIT
	 *    UNCERTAIN  an attempt is claimed and cannot be accounted for; NOT a
	 *               failure, and NOT a reason to wait for ever
	 *    UNRECORDED no answer state was ever recorded for this turn
	 *
	 *  Absent means the server did not classify the turn — never "answered". */
	answerOutlook?: TurnOutlook;
	/** How long the outstanding attempt has been running. Present only with an
	 *  `IN_FLIGHT` outlook, where it is a fact rather than a spinner. */
	answerRunningForMs?: number | null;
	/** WHO WROTE THIS TURN, when the record says. `OWNER_SESSION` is a verified
	 *  Owner session; `OWNER_TOKEN` is the deployment's bearer token, which the
	 *  CLI and every acceptance instrument also hold. Absent means the record
	 *  does not say — which is every row written before migration 0036. */
	provenance?: 'OWNER_SESSION' | 'OWNER_TOKEN' | null;
	references?: {
		references: Array<{ ref: string; kind: string; id: string }>;
		resolved: Array<{
			ref: string;
			kind: string;
			id: string;
			status: string;
			summary: string;
			revision: string;
			drift: 'UNCHANGED' | 'CHANGED' | 'NOT_OBSERVED';
			reason: string;
		}>;
	};
	/** THE ATTACHMENTS THIS TURN CARRIED. Present on an Owner turn that cited
	 *  materials; absent on a turn that carried none and on historical rows.
	 *  Metadata only — the content is fetched from the material route. */
	materials?: MaterialSummary[];
}

/**
 * A RECORD THE OWNER ASKED TO DISCUSS — carried from a structured view into the
 * one conversation, and NOT as text.
 *
 * THE TRANSPORT WAS BUILT IN SLICE 2 AND NOTHING COULD USE IT: the contract, the
 * parser, the server-side resolution and the drift detection all existed, and
 * `sendTurn` accepted `references`, and the composer had no way to produce one.
 * So "Discuss with Scout" pre-filled a sentence and the Owner got a conversation
 * about a record rather than a conversation WITH the record.
 *
 * A DEEP LINK IS HOW IT TRAVELS, rather than module state, and that is a choice
 * about reloads: `/chat/discuss/<ref>` survives a refresh, a bookmark and a
 * second tab, and a signal held in memory survives none of them. The ref is
 * percent-encoded into the path segment, which is what the router already does.
 */
// NAMED WITHOUT THE `SCOUT_` PREFIX, AND THAT IS THE SWEEP'S RULE RATHER THAN A
// PREFERENCE. Every other constant in this repository is `SCOUT_*`, and a
// `SCOUT_A_B` in the workspace is exactly what a Worker BINDING looks like — so
// `scout-web-credential-free.test.ts` refuses the prefix outright, on the
// principle that a name it cannot tell from a binding is one that should not be
// there. The repo's answer to a broad sweep meeting legitimate code is to rename
// the CODE (slice 2 renamed `writeMainChat` to `recordMainChat` for the no-hands
// sweep) rather than to exempt a file, so this is named for what it is.
export const DISCUSS_ROUTE_SECTION = 'discuss';

export function discussPath(ref: string): string {
	return `/chat/${DISCUSS_ROUTE_SECTION}/${encodeURIComponent(ref)}`;
}

export interface MainChatResolution {
	conversationId: string;
	conversation: {
		conversationId: string;
		title: string;
		createdAt: string;
		updatedAt: string;
	} | null;
	/** How it became the Main Chat, and on what grounds — recorded by the
	 *  server, and shown so the decision is not invisible. */
	decidedBy: 'CREATED' | 'ADOPTED';
	evidence: string;
	/** Present ONLY when the server could not identify the Owner's main chat
	 *  and declined to guess. Never an empty array standing in for "nothing to
	 *  consider". */
	candidates?: Array<{ conversationId: string; title: string; turns: number }>;
	messages: ChatMessage[];
	olderCursor: number | null;
	hasOlder: boolean;
}

export interface AnsweredAsk {
	conversationId: string;
	requestState: 'ANSWERED' | 'REPLAYED';
	messageId: string;
	answer: {
		content: string;
		reasoning: string | null;
		phrasing: string;
		completion: string | null;
		refs: string[];
		modelNote: string | null;
	};
}

export interface InFlightAsk {
	conversationId: string;
	requestState: 'IN_FLIGHT';
	ownerMessageId: string;
	question: string;
	note: string;
	runningForMs: number;
}

/** THE RECORD CANNOT SAY WHAT HAPPENED, AND THE RECOVERY IS THE OWNER'S.
 *
 *  Distinct from `IN_FLIGHT` on purpose: one means "wait", and this one means
 *  "waiting will not help and this server will not spend your money deciding
 *  for you". See `core/answer-attempt.ts`. */
export interface UncertainAsk {
	conversationId: string;
	requestState: 'UNCERTAIN';
	ownerMessageId: string;
	question: string;
	note: string;
	recovery: { available: true; how: string };
}

export type TurnOutlook = 'ANSWERED' | 'FAILED' | 'IN_FLIGHT' | 'UNCERTAIN' | 'UNRECORDED';

export type AskOutcome = AnsweredAsk | InFlightAsk | UncertainAsk;

export type AskResult =
	| { ok: true; outcome: AskOutcome }
	| { ok: false; error: GatewayError };

/**
 * A NAME FOR ONE TURN, MINTED BEFORE IT IS SENT.
 *
 * `crypto.randomUUID` where the browser has it — a secure context — and a
 * timestamp-and-randomness fallback where it does not, because a local
 * development origin over plain HTTP is not a secure context and a chat that
 * refuses to send there would be a chat nobody could work on.
 */
export function mintRequestId(): string {
	const cryptoRef = globalThis.crypto as { randomUUID?: () => string } | undefined;
	if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID();
	return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function resolveMainChat(): Promise<GatewayResult<MainChatResolution>> {
	return await readGateway<MainChatResolution>('/api/scout/gateway/conversations/main', {
		method: 'POST',
		body: {},
		// The server may have to read a page of history on the first call of a
		// session; it never runs a model, so it is fast.
		timeoutMs: 30_000,
	});
}

export async function adoptMainChat(conversationId: string): Promise<GatewayResult<MainChatResolution>> {
	return await readGateway<MainChatResolution>('/api/scout/gateway/conversations/main', {
		method: 'POST',
		body: { adopt: conversationId },
		timeoutMs: 30_000,
	});
}

/**
 * THE DEDICATED CHAT OF ONE RESEARCH SPACE.
 *
 * It returns the SAME page shape as the Main Chat — conversation, messages,
 * older cursor — so the one Chat surface renders it with the same components.
 * The space id is a path segment, and the server verifies that the resolved
 * conversation is that space's own; a client cannot turn a Main Chat id into a
 * space turn by naming a space.
 */
export interface SpaceChatResolution extends MainChatResolution {
	space: { spaceId: string; name: string; status: string };
	created: boolean;
}

export async function resolveSpaceChat(
	spaceId: string,
): Promise<GatewayResult<SpaceChatResolution>> {
	return await readGateway<SpaceChatResolution>(
		`/api/scout/gateway/spaces/${encodeURIComponent(spaceId)}/chat`,
		{ method: 'GET', timeoutMs: 30_000 },
	);
}

export async function readOlderMessages(
	conversationId: string,
	beforeSequence: number,
	limit = 40,
): Promise<
	GatewayResult<{
		found: boolean;
		messages: ChatMessage[];
		olderCursor: number | null;
		hasOlder: boolean;
	}>
> {
	return await readGateway(
		`/api/scout/gateway/conversations/${encodeURIComponent(conversationId)}?before=${beforeSequence}&limit=${limit}`,
	);
}

/**
 * SEND A TURN.
 *
 * THE TIMEOUT IS GENEROUS AND THE REASON IS THE PRODUCT'S: `/gateway/ask` can
 * run a model, and in the same turn a research segment — the CLI has always
 * allowed ten minutes for exactly this. A short timeout here would abort work
 * that is still happening and then report it as a failure, which is the lie the
 * request identity exists to prevent.
 */
export async function sendTurn(input: {
	conversationId: string;
	question: string;
	clientRequestId: string;
	materialRefs?: string[];
	references?: Array<{ ref: string; observedRevision?: string; note?: string }>;
	signal?: AbortSignal;
	/** THE OWNER'S EXPLICIT DECISION TO TRY AN UNCERTAIN TURN AGAIN.
	 *
	 *  Sent ONLY from the recovery control the uncertainty state offers, never
	 *  by default and never on an ordinary retry. It is the one thing that lets
	 *  the server start a second attempt on a turn whose first attempt cannot be
	 *  accounted for — and that attempt may have been billed — so it travels
	 *  only when a person has said yes to it. */
	recoverAbandoned?: boolean;
	/** THE RESEARCH SPACE THIS TURN BELONGS TO. Present only on a dedicated
	 *  space chat; the server verifies the conversation is that space's own. */
	spaceId?: string;
}): Promise<AskResult> {
	const result = await readGateway<AskOutcome>('/api/scout/gateway/ask', {
		method: 'POST',
		body: {
			question: input.question,
			conversationId: input.conversationId,
			clientRequestId: input.clientRequestId,
			...(input.spaceId === undefined ? {} : { spaceId: input.spaceId }),
			...(input.recoverAbandoned === true ? { recoverAbandoned: true } : {}),
			...(input.materialRefs === undefined || input.materialRefs.length === 0
				? {}
				: { materialRefs: input.materialRefs }),
			...(input.references === undefined || input.references.length === 0
				? {}
				: { references: input.references }),
		},
		timeoutMs: 600_000,
		...(input.signal === undefined ? {} : { signal: input.signal }),
	});
	if (!result.ok) return { ok: false, error: result.error };
	return { ok: true, outcome: result.data as AskOutcome };
}

/** ONE OWNER-CLICKED SCOUT TURN. No question is sent: the click is the event,
 *  and the server records it as the trigger of a SCOUT row rather than as an
 *  Owner message. The request id is the idempotency key for a retry. */
export interface ScoutTurnOutcome {
	written: boolean;
	duplicate: boolean;
	conversationId: string;
	message: {
		messageId: string;
		sequence: number;
		role: string;
		content: string;
		createdAt: string;
		triggerKind: string | null;
	} | null;
	reason?: string;
}

export async function scoutTurn(input: {
	clientRequestId: string;
}): Promise<
	| { ok: true; data: ScoutTurnOutcome }
	| { ok: false; error: GatewayError }
> {
	const result = await readGateway<ScoutTurnOutcome>('/api/scout/gateway/chat/scout-turn', {
		method: 'POST',
		body: { clientRequestId: input.clientRequestId },
		timeoutMs: 240_000,
	});
	if (!result.ok) return { ok: false, error: result.error };
	return { ok: true, data: result.data };
}

export async function ingestMaterial(input: {
	content: string;
	kind: string;
	title?: string;
}): Promise<
	GatewayResult<{
		accepted: boolean;
		reason?: string;
		material?: {
			materialId: string;
			kind: string;
			title: string;
			byteSize: number;
			lineCount: number;
			chunkCount: number;
		};
		alreadyHeld?: boolean;
	}>
> {
	return await readGateway('/api/scout/gateway/materials', {
		method: 'POST',
		body: input,
		timeoutMs: 120_000,
	});
}

/** How much of a turn is shown before it is collapsed. Long material pasted
 *  into the composer produces long turns, and a wall of text should not push
 *  every other turn off the screen — but nothing is ever hidden without a way
 *  to open it. */
export const LONG_TURN_CHARS = 1_200;

export function isLong(content: string): boolean {
	return content.length > LONG_TURN_CHARS;
}

/**
 * WHEN A TURN HAPPENED, SAID THE WAY SOMEBODY WOULD SAY IT.
 *
 * THE RECORD'S OWN FORM IS STILL THERE AND STILL EXACT. Every turn head renders
 * `<time dateTime={createdAt} title={createdAt}>` — the attribute a machine
 * reads and the tooltip a person gets — so nothing is lost by not PRINTING
 * `2026-10-16T09:19:52.000Z` into the conversation. What was lost by printing
 * it is the conversation: a wall of ISO-8601 with milliseconds in it is a
 * machine's timestamp wearing a reader's clothes, and on a phone it was the
 * widest single element in the turn header.
 *
 * A DATE THAT WILL NOT PARSE IS RETURNED UNCHANGED rather than rendered as
 * "Invalid Date". This is a record surface; the honest fallback for a value
 * this code does not understand is the value itself.
 *
 * THE LOCALE IS THE READER'S, because the question "when was this" is answered
 * in the reader's own conventions. The instant does not depend on it — that is
 * what `dateTime` is for.
 */
export function formatTurnTime(createdAt: string): string {
	const at = new Date(createdAt);
	if (Number.isNaN(at.getTime())) return createdAt;
	return new Intl.DateTimeFormat(undefined, {
		day: 'numeric',
		month: 'short',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	}).format(at);
}

// --------------------------------------------------------------- file material

export interface UploadedMaterial {
	materialId: string;
	fileName: string;
	mediaType: string;
	byteSize: number;
	extractionStatus: 'TEXT_EXTRACTED' | 'IMAGE' | 'IMAGE_ONLY' | 'UNSUPPORTED' | 'FAILED';
	extractionNote: string;
	detail: {
		pageCount?: number;
		sheetNames?: string[];
		slideCount?: number;
		width?: number;
		height?: number;
		warnings?: string[];
		truncated?: boolean;
	};
}

export interface UploadMaterialResponse {
	accepted: boolean;
	reason?: string;
	material?: UploadedMaterial;
	alreadyHeld?: boolean;
}

interface WebEnvelope<T> {
	success: boolean;
	data?: T;
	message?: string;
	error?: { message?: string; name?: string };
}

function parseEnvelope<T>(status: number, text: string): GatewayResult<T> {
	let body: WebEnvelope<T>;
	try {
		body = JSON.parse(text) as WebEnvelope<T>;
	} catch {
		return {
			ok: false,
			error: {
				name: 'ScoutMalformedResponse',
				message: `Scout answered ${String(status)} with a body that is not the gateway envelope.`,
				status,
			},
		};
	}
	if (status >= 200 && status < 300 && body.success && body.data !== undefined) {
		return { ok: true, data: body.data };
	}
	return {
		ok: false,
		error: {
			name: body.error?.name ?? 'ScoutRequestFailed',
			message: body.error?.message ?? body.message ?? `Scout answered ${String(status)}.`,
			status,
		},
	};
}

/**
 * UPLOAD ONE FILE, WITH PROGRESS.
 *
 * `fetch` HAS NO UPLOAD PROGRESS, and a 10 MB PDF over a phone connection with
 * no progress is indistinguishable from a hang. XHR is the platform's only
 * upload-progress API, so it is used here deliberately; the response parsing is
 * the same envelope contract every other route uses.
 */
export function uploadMaterialFile(
	file: File,
	options: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<GatewayResult<UploadMaterialResponse>> {
	return new Promise((resolve) => {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', '/api/scout/gateway/materials/file');
		xhr.withCredentials = true;
		xhr.responseType = 'text';
		xhr.timeout = 180_000;
		const onAbort = (): void => xhr.abort();
		options.signal?.addEventListener('abort', onAbort, { once: true });
		xhr.upload.onprogress = (event) => {
			if (event.lengthComputable && event.total > 0) {
				options.onProgress?.(event.loaded / event.total);
			}
		};
		xhr.onload = () => {
			options.signal?.removeEventListener('abort', onAbort);
			resolve(parseEnvelope<UploadMaterialResponse>(xhr.status, xhr.responseText));
		};
		xhr.onerror = () => {
			options.signal?.removeEventListener('abort', onAbort);
			resolve({
				ok: false,
				error: { name: 'ScoutNetworkError', message: 'The upload could not reach Scout.', status: 0 },
			});
		};
		xhr.ontimeout = () => {
			options.signal?.removeEventListener('abort', onAbort);
			resolve({
				ok: false,
				error: { name: 'ScoutUploadTimeout', message: 'The upload did not finish in time. Nothing is known about what Scout stored; try again and it will be recognised if it is already held.', status: 0 },
			});
		};
		xhr.onabort = () => {
			options.signal?.removeEventListener('abort', onAbort);
			resolve({
				ok: false,
				error: { name: 'ScoutRequestAbandoned', message: 'The upload was stopped before it finished.', status: 0 },
			});
		};
		const form = new FormData();
		form.append('file', file, file.name);
		xhr.send(form);
	});
}

/** THE ORIGINAL FILE AS BYTES — for an image preview or a download. The route
 *  is owner-authenticated and answers no-store. */
export async function loadMaterialBlob(
	materialId: string,
	signal?: AbortSignal,
): Promise<{ ok: true; blob: Blob } | { ok: false; error: GatewayError }> {
	let response: Response;
	try {
		response = await fetch(
			`/api/scout/gateway/materials/${encodeURIComponent(materialId)}/file`,
			{ credentials: 'same-origin', ...(signal === undefined ? {} : { signal }) },
		);
	} catch (error) {
		return {
			ok: false,
			error: {
				name: error instanceof DOMException && error.name === 'AbortError' ? 'ScoutRequestAbandoned' : 'ScoutNetworkError',
				message: 'The file could not be read from Scout.',
				status: 0,
			},
		};
	}
	if (!response.ok) {
		const text = await response.text().catch(() => '');
		const parsed = parseEnvelope<unknown>(response.status, text);
		return parsed.ok
			? { ok: false, error: { name: 'ScoutRequestFailed', message: `Scout answered ${String(response.status)}.`, status: response.status } }
			: parsed;
	}
	return { ok: true, blob: await response.blob() };
}

/** THE MATERIAL'S TEXT, page by page, joined. Bounded by the route's own page
 *  size and the material's stored ceiling; a preview may show only part of what
 *  is returned, but the store is never truncated by this read. */
export async function readMaterialText(
	materialId: string,
): Promise<GatewayResult<string>> {
	let from = 0;
	let text = '';
	for (let page = 0; page < 500; page += 1) {
		const result = await readGateway<{
			found: boolean;
			chunks: Array<{ chunkIndex: number; content: string }>;
			nextFrom: number | null;
		}>(
			`/api/scout/gateway/materials/${encodeURIComponent(materialId)}?from=${String(from)}&limit=50`,
			{ timeoutMs: 60_000 },
		);
		if (!result.ok) return result;
		if (!result.data.found) {
			return {
				ok: false,
				error: {
					name: 'ScoutMaterialNotFound',
					message: 'Scout no longer holds this material.',
					status: 404,
				},
			};
		}
		text += result.data.chunks.map((chunk) => chunk.content).join('');
		if (result.data.nextFrom === null) return { ok: true, data: text };
		from = result.data.nextFrom;
	}
	return {
		ok: false,
		error: {
			name: 'ScoutMaterialTooLarge',
			message: 'The material has more pages than this preview will read. Download the original instead.',
			status: 0,
		},
	};
}

export async function deleteMaterial(
	materialId: string,
): Promise<GatewayResult<{ deleted: boolean }>> {
	return await readGateway<{ deleted: boolean }>(
		`/api/scout/gateway/materials/${encodeURIComponent(materialId)}`,
		{ method: 'DELETE', timeoutMs: 30_000 },
	);
}

/** A readable size for a chip. Decimal units, because the bound is stated in
 *  bytes and a reader comparing "1.2 MB" with "2,000,000 bytes" should not have
 *  to convert between two conventions. */
export function formatBytes(bytes: number): string {
	if (!Number.isFinite(bytes) || bytes < 0) return `${String(bytes)} bytes`;
	if (bytes < 1_000) return `${String(bytes)} bytes`;
	if (bytes < 1_000_000) return `${(bytes / 1_000).toFixed(1)} KB`;
	return `${(bytes / 1_000_000).toFixed(1)} MB`;
}
